/**
 * FlagOutcomeSummary — per-flag outcome of an edit-highlight rewrite,
 * shown above the proposal card: which ``[MISSING INFO: …]`` flags in the
 * writer's selection the rewrite filled in, and which it kept (the library
 * had no answer). Computed client-side by comparing the markers in the
 * original selection with those in the proposed replacement.
 *
 * Renders nothing when the original selection had no missing-info flags.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useMemo } from "react";
import { summarizeFlagOutcome } from "../services/missingInfoProgress";
import { fonts, inkColors, statusColors, surfaceColors, typeScale } from "../tokens";

const useStyles = makeStyles({
  root: {
    margin: 0,
    padding: "8px 12px",
    borderRadius: "8px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
    fontSize: typeScale.label,
    color: inkColors.ink2,
  },
  headline: { fontWeight: 600, marginBottom: "4px" },
  list: { listStyleType: "none", margin: 0, padding: 0 },
  item: { display: "flex", gap: "6px", alignItems: "baseline", padding: "1px 0" },
  dot: { width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, alignSelf: "center" },
  dotResolved: { background: statusColors.cool },
  dotKept: { background: statusColors.warn },
  tag: { fontFamily: fonts.mono, color: inkColors.ink4, flexShrink: 0 },
  body: { color: inkColors.ink3, wordBreak: "break-word" },
});

export interface FlagOutcomeSummaryProps {
  originalText: string;
  replacementText: string;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export const FlagOutcomeSummary: React.FC<FlagOutcomeSummaryProps> = ({ originalText, replacementText }) => {
  const styles = useStyles();
  const outcome = useMemo(() => summarizeFlagOutcome(originalText, replacementText), [originalText, replacementText]);
  const total = outcome.resolved.length + outcome.kept.length;
  if (total === 0) return null;

  const headline =
    outcome.resolved.length === 0
      ? `None of the ${plural(total, "missing-info flag")} could be resolved from your library`
      : `Resolved ${outcome.resolved.length} of ${plural(total, "missing-info flag")}` +
        (outcome.kept.length ? ` · ${outcome.kept.length} kept` : "");

  return (
    <section className={styles.root} aria-label="Missing-info flag outcome">
      <div className={styles.headline}>{headline}</div>
      <ul className={styles.list}>
        {outcome.resolved.map((flag, i) => (
          <li key={`r${i}`} className={styles.item}>
            <span className={mergeClasses(styles.dot, styles.dotResolved)} aria-hidden="true" />
            <span className={styles.tag}>Resolved</span>
            <span className={styles.body}>{flag || "missing info"}</span>
          </li>
        ))}
        {outcome.kept.map((flag, i) => (
          <li key={`k${i}`} className={styles.item}>
            <span className={mergeClasses(styles.dot, styles.dotKept)} aria-hidden="true" />
            <span className={styles.tag}>Kept</span>
            <span className={styles.body}>{flag || "missing info"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FlagOutcomeSummary;
